import React, { useEffect, useState } from "react";
import { getApiBase } from "../utils/paths";
import styles from "./Dashboard.module.css";

function Icon({ name, size = 20 }) {
  const props = {
    width: size,
    height: size,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 2,
    strokeLinecap: "round",
    strokeLinejoin: "round",
  };

  switch (name) {
    case "briefcase":
      return (
        <svg {...props}>
          <rect x="2" y="7" width="20" height="14" rx="2" />
          <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
        </svg>
      );
    case "cap":
      return (
        <svg {...props}>
          <path d="M22 10 12 5 2 10l10 5 10-5Z" />
          <path d="M6 12v5c0 1.5 3 3 6 3s6-1.5 6-3v-5" />
        </svg>
      );
    case "doc":
      return (
        <svg {...props}>
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z" />
          <path d="M14 2v6h6" />
        </svg>
      );
    case "layout":
      return (
        <svg {...props}>
          <rect x="3" y="3" width="18" height="18" rx="2" />
          <path d="M3 9h18M9 21V9" />
        </svg>
      );
    case "users":
      return (
        <svg {...props}>
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
        </svg>
      );
    case "user":
      return (
        <svg {...props}>
          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
          <circle cx="12" cy="7" r="4" />
        </svg>
      );
    case "check":
      return (
        <svg {...props}>
          <circle cx="12" cy="12" r="10" />
          <path d="m9 12 2 2 4-4" />
        </svg>
      );
    case "bars":
      return (
        <svg {...props}>
          <path d="M4 20V10M12 20V4M20 20v-7" />
        </svg>
      );
    case "heart":
      return (
        <svg {...props}>
          <path d="M12 21s-6.7-4.35-9.3-8.1C.9 10.1 1.6 6.4 4.7 5.1 7 4.1 9.4 5 12 7.7 14.6 5 17 4.1 19.3 5.1c3.1 1.3 3.8 5 1.99 7.8C18.7 16.65 12 21 12 21Z" />
        </svg>
      );
    default:
      return null;
  }
}

function Sparkline({ data, color }) {
  const w = 140;
  const h = 44;
  const max = Math.max(...data, 0);
  const min = Math.min(...data, 0);
  const range = max - min || 1;
  const step = w / Math.max(data.length - 1, 1);
  const points = data.map((value, index) => {
    const x = index * step;
    const y = h - ((value - min) / range) * (h - 6) - 3;
    return [x, y];
  });
  const linePath = points
    .map((point, index) => (index === 0 ? `M${point[0]},${point[1]}` : `L${point[0]},${point[1]}`))
    .join(" ");
  const areaPath = `${linePath} L${w},${h} L0,${h} Z`;
  const gradId = `spark-${color.replace("#", "")}`;

  return (
    <svg viewBox={`0 0 ${w} ${h}`} className={styles.sparkSvg} preserveAspectRatio="none">
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.35" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={areaPath} fill={`url(#${gradId})`} stroke="none" />
      <path d={linePath} fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function OverviewChart({ series }) {
  const [hoverIdx, setHoverIdx] = useState(series.labels.length - 1);
  const w = 760;
  const h = 260;
  const padL = 36;
  const padB = 26;
  const allValues = series.lines.flatMap((line) => line.data);
  const max = Math.max(...allValues, 1);
  const stepX = series.labels.length > 1 ? (w - padL) / (series.labels.length - 1) : 0;
  const yFor = (value) => h - padB - (value / max) * (h - padB - 10);
  const xFor = (index) => padL + index * stepX;

  return (
    <div className={styles.overviewChartWrap}>
      <svg viewBox={`0 0 ${w} ${h}`} className={styles.overviewSvg}>
        {[0, 0.4, 0.8, 1].map((percent) => {
          const value = Math.round(max * percent);
          const y = yFor(value);
          return (
            <g key={percent}>
              <line x1={padL} x2={w} y1={y} y2={y} className={styles.gridLine} />
              <text x={0} y={y + 4} className={styles.axisLabel}>
                {value}
              </text>
            </g>
          );
        })}
        {series.labels.map((label, index) => (
          <text key={label} x={xFor(index)} y={h - 4} textAnchor="middle" className={styles.axisLabel}>
            {label}
          </text>
        ))}
        {hoverIdx !== null && <line x1={xFor(hoverIdx)} x2={xFor(hoverIdx)} y1={0} y2={h - padB} className={styles.hoverLine} />}
        {series.lines.map((line) => {
          const path = line.data
            .map((value, index) => `${index === 0 ? "M" : "L"}${xFor(index)},${yFor(value)}`)
            .join(" ");
          return (
            <g key={line.key}>
              <path d={path} fill="none" stroke={line.color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
              {line.data.map((value, index) => (
                <circle
                  key={index}
                  cx={xFor(index)}
                  cy={yFor(value)}
                  r={hoverIdx === index ? 5 : 3.5}
                  fill="#fff"
                  stroke={line.color}
                  strokeWidth="2.5"
                  onMouseEnter={() => setHoverIdx(index)}
                  style={{ cursor: "pointer" }}
                />
              ))}
            </g>
          );
        })}
        {series.labels.map((_, index) => (
          <rect
            key={index}
            x={xFor(index) - stepX / 2}
            y={0}
            width={stepX}
            height={h - padB}
            fill="transparent"
            onMouseEnter={() => setHoverIdx(index)}
          />
        ))}
      </svg>
      {hoverIdx !== null && series.labels[hoverIdx] && (
        <div className={styles.chartTooltip} style={{ left: `${(xFor(hoverIdx) / w) * 100}%` }}>
          <div className={styles.tooltipDate}>{series.labels[hoverIdx]}</div>
          {series.lines.map((line) => (
            <div key={line.key} className={styles.tooltipRow}>
              <span className={styles.tooltipDot} style={{ background: line.color }} />
              <span className={styles.tooltipName}>{line.name}</span>
              <span className={styles.tooltipValue}>{line.data[hoverIdx]}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Donut({ segments, total }) {
  const size = 160;
  const stroke = 22;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  let offset = 0;

  return (
    <div className={styles.donutWrap}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <g transform={`rotate(-90 ${size / 2} ${size / 2})`}>
          {segments.map((segment) => {
            const dash = (segment.pct / 100) * circumference;
            const circle = (
              <circle
                key={segment.label}
                cx={size / 2}
                cy={size / 2}
                r={radius}
                fill="none"
                stroke={segment.color}
                strokeWidth={stroke}
                strokeDasharray={`${dash} ${circumference - dash}`}
                strokeDashoffset={-offset}
                strokeLinecap="butt"
              />
            );
            offset += dash;
            return circle;
          })}
        </g>
      </svg>
      <div className={styles.donutCenter}>
        <div className={styles.donutTotal}>{total}</div>
        <div className={styles.donutLabel}>Total</div>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const [dashboard, setDashboard] = useState({
    counts: {
      careers: 0,
      internships: 0,
      applicants: 0,
      blogs: 0,
      events: 0,
      gallery: 0,
      reports: 0,
      donations: 0,
    },
    donation_status: {
      Pending: 0,
      Verified: 0,
      Rejected: 0,
    },
    recent_activities: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const adminName = (typeof window !== "undefined" && localStorage.getItem("adminEmail")) || "Administrator";

  useEffect(() => {
    async function fetchDashboard() {
      setLoading(true);
      setError("");
      try {
        const token = localStorage.getItem("adminToken") || "";
        const res = await fetch(`${getApiBase()}/dashboard/index.php`, {
          headers: {
            Authorization: `Bearer ${token}`,
            "X-Auth-Token": token,
          },
        });
        if (!res.ok) {
          const data = await res.json().catch(() => null);
          throw new Error(data?.error || `Failed to load dashboard data (${res.status})`);
        }
        const data = await res.json();
        setDashboard({
          counts: data.counts || dashboard.counts,
          donation_status: data.donation_status || dashboard.donation_status,
          recent_activities: data.recent_activities || [],
        });
      } catch (err) {
        if (err?.message?.includes("401") || err?.message?.includes("Unauthorized") || err?.message?.includes("Invalid or expired token")) {
          localStorage.removeItem("adminToken");
          localStorage.removeItem("adminEmail");
          window.location.replace("/admin/login");
          return;
        }
        setError(err.message || "Unable to load dashboard data");
      } finally {
        setLoading(false);
      }
    }
    fetchDashboard();
  }, []);

  const cards = [
    {
      label: "Career Jobs",
      value: dashboard.counts.careers,
      color: "#6d5bf6",
      icon: "briefcase",
      spark: [2, 4, 6, 5, 7, 8, dashboard.counts.careers],
    },
    {
      label: "Internships",
      value: dashboard.counts.internships,
      color: "#22c07a",
      icon: "cap",
      spark: [1, 3, 4, 5, 6, 7, dashboard.counts.internships],
    },
    {
      label: "Applications",
      value: dashboard.counts.applicants,
      color: "#f5923e",
      icon: "doc",
      spark: [6, 10, 14, 18, 20, 24, dashboard.counts.applicants],
    },
    {
      label: "Blogs",
      value: dashboard.counts.blogs,
      color: "#4a90f7",
      icon: "layout",
      spark: [1, 2, 2, 3, 4, 4, dashboard.counts.blogs],
    },
  ];

  const overviewSeries = {
    labels: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
    lines: [
      {
        key: "careers",
        name: "Career Jobs",
        color: "#6d5bf6",
        data: new Array(7).fill(dashboard.counts.careers),
      },
      {
        key: "internships",
        name: "Internships",
        color: "#22c07a",
        data: new Array(7).fill(dashboard.counts.internships),
      },
      {
        key: "applications",
        name: "Applications",
        color: "#f5923e",
        data: new Array(7).fill(dashboard.counts.applicants),
      },
      {
        key: "blogs",
        name: "Blogs",
        color: "#4a90f7",
        data: new Array(7).fill(dashboard.counts.blogs),
      },
    ],
  };

  const totalDonations = dashboard.counts.donations;
  const donationSegments = [
    {
      label: "Pending",
      pct: totalDonations ? Math.round((dashboard.donation_status.Pending / totalDonations) * 100) : 0,
      color: "#f59e0b",
    },
    {
      label: "Verified",
      pct: totalDonations ? Math.round((dashboard.donation_status.Verified / totalDonations) * 100) : 0,
      color: "#16a34a",
    },
    {
      label: "Rejected",
      pct: totalDonations ? Math.round((dashboard.donation_status.Rejected / totalDonations) * 100) : 0,
      color: "#ef4444",
    },
  ];

  return (
    <div className={styles.dashboard}>
      {error && <div className={styles.alert}>{error}</div>}

      <div className={styles.heroCard}>
        <div className={styles.heroLeft}>
          <p className={styles.greeting}>Welcome back,</p>
          <h1 className={styles.heroTitle}>{adminName} 👋</h1>
          <p className={styles.heroText}>Your dashboard is now powered by real backend data.</p>
        </div>
        <div className={styles.heroBadge}>
          <div className={styles.heroBadgeTitle}>Live admin insights</div>
          <p className={styles.heroBadgeText}>Track content, donations, and activity in real time.</p>
        </div>
      </div>

      <div className={styles.statsGrid}>
        {cards.map((card) => (
          <article key={card.label} className={styles.statCard}>
            <div className={styles.statTop}>
              <div className={styles.statIcon} style={{ background: card.color }}>
                <Icon name={card.icon} size={20} />
              </div>
              <div>
                <div className={styles.statLabel}>{card.label}</div>
                <div className={styles.statValue}>{card.value}</div>
              </div>
            </div>
            <div className={styles.statBottom}>
              <span className={styles.statChange}>{card.value > 0 ? `${Math.round((card.value / Math.max(card.value, 1)) * 100)}%` : "0%"}</span>
              <Sparkline data={card.spark} color={card.color} />
            </div>
          </article>
        ))}
      </div>

      <div className={styles.bottomGrid}>
        <section className={styles.overviewCard}>
          <div className={styles.overviewHeader}>
            <h2>Overview</h2>
            <div className={styles.legend}>
              {overviewSeries.lines.map((line) => (
                <span key={line.key} className={styles.legendItem}>
                  <span className={styles.legendDot} style={{ background: line.color }} />
                  {line.name}
                </span>
              ))}
            </div>
          </div>
          <OverviewChart series={overviewSeries} />
        </section>

        <section className={styles.smallCard}>
          <div className={styles.cardHeader}>
            <h3>Donation Status</h3>
          </div>
          <div className={styles.donutSection}>
            <Donut segments={donationSegments} total={totalDonations} />
            <ul className={styles.donutLegendList}>
              {donationSegments.map((segment) => (
                <li key={segment.label}>
                  <span className={styles.legendDot} style={{ background: segment.color }} />
                  <span className={styles.donutLegendLabel}>{segment.label}</span>
                  <span className={styles.donutLegendPct}>{segment.pct}%</span>
                </li>
              ))}
            </ul>
          </div>
        </section>
      </div>

      <section className={styles.smallCard}>
        <div className={styles.cardHeader}>
          <h3>Recent Activity</h3>
          <span className={styles.viewAll}>Live feed</span>
        </div>
        <ul className={styles.activityList}>
          {loading ? (
            <li className={styles.activityItem}>Loading live data…</li>
          ) : dashboard.recent_activities.length ? (
            dashboard.recent_activities.map((activity, index) => (
              <li key={`${activity.title}-${index}`} className={styles.activityItem}>
                <span className={styles.activityIcon} style={{ background: `${activity.color}1A`, color: activity.color }}>
                  <Icon name={activity.type === "donation" ? "heart" : activity.type === "application" ? "user" : "layout"} size={18} />
                </span>
                <div className={styles.activityText}>
                  <div className={styles.activityTitle}>{activity.title}</div>
                  <div className={styles.activitySubtitle}>{activity.subtitle}</div>
                </div>
                <span className={styles.activityTime}>{activity.created_at}</span>
              </li>
            ))
          ) : (
            <li className={styles.activityItem}>No recent activity found.</li>
          )}
        </ul>
      </section>
    </div>
  );
}
